import { useState } from 'react'
import { Navigate, useNavigate } from 'react-router'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import Cards from '@cloudscape-design/components/cards'
import Spinner from '@cloudscape-design/components/spinner'
import Page from '@/components/common/Page'
import { useClients } from '@/hooks/useClients'
import { SELECTED_CLIENT_STORAGE_KEY } from '@/lib/clientStorage'

export default function ClientSelectorPage() {
  const navigate = useNavigate()
  const { clients, loading, error } = useClients()
  const [storedClientId, setStoredClientId] = useState<string | null>(
    () => localStorage.getItem(SELECTED_CLIENT_STORAGE_KEY),
  )

  // If we have a stored id but are at /, redirect to /clients/:id
  if (!storedClientId) {
    // Don't redirect yet - let user choose
  } else {
    // Redirect only on first load
    if (storedClientId && clients.length > 0) {
      return <Navigate to={`/clients/${storedClientId}`} replace />
    }
  }

  const handleSelectClient = (id: string) => {
    localStorage.setItem(SELECTED_CLIENT_STORAGE_KEY, id)
    setStoredClientId(id)
    navigate(`/clients/${id}`)
  }

  if (loading) {
    return (
      <Page title="Workspace">
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
        </Box>
      </Page>
    )
  }

  if (error) {
    return (
      <Page title="Workspace">
        <Box textAlign="center" padding="xxl">
          <Box variant="p" color="text-status-error">
            Failed to load clients.
          </Box>
        </Box>
      </Page>
    )
  }

  return (
    <Page
      title="Workspace"
      actions={
        <Button disabled variant="primary">
          New Client (coming soon)
        </Button>
      }
    >
      <Cards
        items={clients}
        cardDefinition={{
          header: (client) => (
            <Box variant="h3">
              {client.agentName ?? client.agent_name ?? client.brandName ?? client.brand_name}
            </Box>
          ),
          sections: [
            {
              id: 'industry',
              header: 'Industry',
              content: (client) => <Box>{client.industry ?? '—'}</Box>,
            },
            {
              id: 'action',
              content: (client) => (
                <Button onClick={() => handleSelectClient(client.id)}>
                  Open
                </Button>
              ),
            },
          ],
        }}
        empty={
          <Box textAlign="center" padding="l">
            <Box variant="p">No clients yet.</Box>
          </Box>
        }
      />
    </Page>
  )
}
